import Telemetry from '@atlassian/mpac-telemetry';
import { SearchTask, searchTaskName } from '../../AddonDiscovery/telemetry/tasks';
import { search } from '../utils/algolia';
import { generateSearchId } from '../utils/ids';
import resolveDataErrorMessages from '../utils/resolveDataErrorMessages';
export const RECEIVE_FACETS = 'SEARCH_RECEIVE_FACETS';
export const FACETS_DATA_ERROR = 'SEARCH_FACETS_DATA_ERROR';
export const RETRIEVE_FRESH_SEARCH_RESULTS = 'SEARCH_RETRIEVE_FRESH_SEARCH_RESULTS';
export const RECEIVE_FRESH_SEARCH_RESULTS = 'SEARCH_RECEIVE_FRESH_SEARCH_RESULTS';
export const DATA_ERROR_FRESH_SEARCH_RESULTS = 'SEARCH_DATA_ERROR_FRESH_SEARCH_RESULTS';
export const RETRIEVE_MORE_SEARCH_RESULTS = 'SEARCH_RETRIEVE_MORE_SEARCH_RESULTS';
export const RECEIVE_MORE_SEARCH_RESULTS = 'SEARCH_RECEIVE_MORE_SEARCH_RESULTS';
export const DATA_ERROR_MORE_SEARCH_RESULTS = 'SEARCH_DATA_ERROR_MORE_SEARCH_RESULTS';
function retrieveFreshSearchResults(_ref) {
  let {
    searchId,
    reducerId
  } = _ref;
  return {
    type: RETRIEVE_FRESH_SEARCH_RESULTS,
    searchId,
    reducerId
  };
}
function receiveFreshSearchResults(_ref2) {
  let {
    searchId,
    searchResults,
    reducerId
  } = _ref2;
  return {
    type: RECEIVE_FRESH_SEARCH_RESULTS,
    searchId,
    searchResults,
    reducerId
  };
}
function dataErrorFreshSearchResults(_ref3) {
  let {
    dataErrorMessages,
    reducerId
  } = _ref3;
  return {
    type: DATA_ERROR_FRESH_SEARCH_RESULTS,
    dataErrorMessages,
    reducerId
  };
}
function retrieveMoreSearchResults(_ref4) {
  let {
    reducerId
  } = _ref4;
  return {
    type: RETRIEVE_MORE_SEARCH_RESULTS,
    reducerId
  };
}
function receiveMoreSearchResults(_ref5) {
  let {
    page,
    searchResults,
    reducerId
  } = _ref5;
  return {
    type: RECEIVE_MORE_SEARCH_RESULTS,
    page,
    searchResults,
    reducerId
  };
}
function dataErrorMoreSearchResults(_ref6) {
  let {
    dataErrorMessages,
    reducerId
  } = _ref6;
  return {
    type: DATA_ERROR_MORE_SEARCH_RESULTS,
    dataErrorMessages,
    reducerId
  };
}
function receiveFacets(_ref7) {
  let {
    facets,
    reducerId
  } = _ref7;
  return {
    type: RECEIVE_FACETS,
    facets,
    reducerId
  };
}
function facetsDataError(_ref8) {
  let {
    dataErrorMessages,
    reducerId
  } = _ref8;
  return {
    type: FACETS_DATA_ERROR,
    dataErrorMessages,
    reducerId
  };
}
const HITS_PER_PAGE = 24;

/**
 * Function to do algolia search and dispatch proper actions
 * @param algoliaClient
 * @param applyOverrides
 * @param environment
 * @param filters
 * @param resolveAlgoliaQueryFilterOverrides
 * @param forReducerId - This is an identifier tag, used to identify to which reducer this action belongs to.
 *                       This helps when we have multiple reducers using the same action names.
 * @returns {function(*)}
 */
export function doFreshSearch(_ref9) {
  let {
    algoliaClient,
    applyOverrides,
    environment,
    filters,
    resolveAlgoliaQueryFilterOverrides,
    featureFlags
  } = _ref9;
  let {
    forReducerId
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    forReducerId: undefined
  };
  return dispatch => {
    SearchTask.start();
    const searchId = generateSearchId();
    dispatch(retrieveFreshSearchResults({
      searchId,
      reducerId: forReducerId
    }));
    search({
      algoliaClient,
      applyOverrides,
      resolveAlgoliaQueryFilterOverrides,
      environment,
      filters,
      numHits: HITS_PER_PAGE,
      page: 0,
      featureFlags
    }).then(data => {
      dispatch(receiveFacets({
        facets: data.get('facets'),
        reducerId: forReducerId
      }));
      dispatch(receiveFreshSearchResults({
        searchId,
        searchResults: data.get('searchResults'),
        reducerId: forReducerId
      }));
      SearchTask.succeeded();
    }, error => {
      if (error.debugData) {
        console.error('Search error, debug data: ');
        console.error(error.debugData);
      }
      const dataErrorMessages = resolveDataErrorMessages(error);
      dispatch(facetsDataError({
        dataErrorMessages,
        reducerId: forReducerId
      }));
      dispatch(dataErrorFreshSearchResults({
        dataErrorMessages,
        reducerId: forReducerId
      }));
      Telemetry.setUFOInteractionError({
        name: searchTaskName,
        errorMessage: error === null || error === void 0 ? void 0 : error.message,
        errorDetail: error
      });
      SearchTask.failed(error);
    });
  };
}

/**
 * Function to get more results from the algolia search
 * @param algoliaClient
 * @param applyOverrides
 * @param environment
 * @param filters
 * @param nextPage
 * @param forReducerId - This is an identifier tag, used to identify to which reducer this action belongs to.
 *                       This helps when we have multiple reducers using the same action names.
 * @returns {function(*)}
 */
export function loadMoreResults(_ref10) {
  let {
    algoliaClient,
    applyOverrides,
    environment,
    filters,
    nextPage,
    resolveAlgoliaQueryFilterOverrides,
    featureFlags
  } = _ref10;
  let {
    forReducerId
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    forReducerId: undefined
  };
  return dispatch => {
    dispatch(retrieveMoreSearchResults({
      reducerId: forReducerId
    }));
    search({
      algoliaClient,
      applyOverrides,
      environment,
      filters,
      numHits: HITS_PER_PAGE,
      page: nextPage,
      resolveAlgoliaQueryFilterOverrides,
      featureFlags
    }).then(data => {
      dispatch(receiveFacets({
        facets: data.get('facets'),
        reducerId: forReducerId
      }));
      dispatch(receiveMoreSearchResults({
        page: nextPage,
        searchResults: data.get('searchResults'),
        reducerId: forReducerId
      }));
    }, error => {
      if (error.debugData) {
        console.error('Search error, debug data: ');
        console.error(error.debugData);
      }
      const dataErrorMessages = resolveDataErrorMessages(error);
      dispatch(dataErrorMoreSearchResults({
        dataErrorMessages,
        reducerId: forReducerId
      }));
    });
  };
}